import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertTriangle, ArrowLeft, Check, ChevronDown, History, MapPin, Shirt } from 'lucide-react';
import {
  getPublicProduct,
  getImageVerification,
  PublicProduct,
  PublicMainProduct,
  CollectionField,
  ImageVerification,
} from '../../services/publicProductService';
import ImageCarousel, { ImageCarouselRef } from '../../components/product/ImageCarousel';
import CreatorInfo from '../../components/product/CreatorInfo';
import ShortcutBar, { Shortcut } from '../../components/shopper/ShortcutBar';
import ShopperLoading from '../../components/shopper/ShopperLoading';
import EmptyHanger from '../../components/shopper/EmptyHanger';
import { BrandColours, BrandLabel, brandBackground } from '../../components/shopper/Brand';
import '../../styles/shopper.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import VersionHistoryWidget from '../../components/product/VersionHistoryWidget';
import ProductActions, { TrustState } from '../../components/product/ProductActions';
import ConsumerAuthSheet from '../../components/vault/ConsumerAuthSheet';
import SaveSheet from '../../components/vault/SaveSheet';
import ConfirmSheet from '../vault/components/ConfirmSheet';
import VerifiedSheet, { VerifiedVersions } from '../vault/components/VerifiedSheet';
import { careSymbols, splitName } from '../vault/pieces';
import CareSymbol, { CARE_LABELS } from '../vault/components/CareSymbol';
import { useThemeChrome, useVaultTheme, VaultThemeContext } from '../vault/useVaultTheme';
import { careLines, groupFields } from './productSections';
import {
  saveItem,
  removeItem,
  setBrandAllowed,
  updateItem,
  vaultErrorMessage,
  VaultIntent,
  VAULT_INTENT_KEY,
} from '../../services/vaultService';
import { showToast } from '../../components/ui/Toast';
import { useAuth } from '../../auth/AuthContext';
import { extractFields, formatValue } from '../../utils/dataUtils';
import { getFieldKey } from '../../utils/fieldKeys';
import { productNameField } from '../../utils/displayNames';
import ScanInterstitial from './ScanInterstitial';
import { track } from '../../utils/analytics';

import { useProductVersion } from '../../hooks/useProductVersion';
import useIsMobile from '../../hooks/useIsMobile';
import PoweredByBasiQR from '../../components/public/PoweredByBasiQR';
import { EXTERNAL_LINK_PROPS, landingSiteUrl, trackLandingSiteClick } from '../../utils/landingSite';

const BURST_COLOURS = ['#e07b3c', '#f4a261', '#8abebe', '#dbecea'];

// Renders the public product page; scanMode branches sit below all hooks so 'basiqr' renders exactly as before
const ProductContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Contextual back navigation: pages that link here (vault, shared catalog,
  // or any future page) can pass { backTo: { path, label } } via router
  // state. Direct QR-scan visits carry no state, so no button is shown.
  const backTo: { path: string; label: string } | undefined =
    location.state?.backTo && typeof location.state.backTo.path === 'string'
      ? location.state.backTo
      : undefined;
  const [product, setProduct] = useState<PublicProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // On-chain image verification (issue #244). null ⇒ no badge; fetched once per
  // product id after the product loads and never blocks product render.
  const [imageVerification, setImageVerification] = useState<ImageVerification | null>(null);
  // Route id whose product has finished loading (product.id may be an on-chain id, so track the route id)
  const [loadedProductId, setLoadedProductId] = useState<string | null>(null);

  // Consumer Vault: save state + in-page auth sheet
  const { isAuthenticated, isLoading: isAuthLoading, userProfile } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [authSheetOpen, setAuthSheetOpen] = useState(false);
  const [confirmUnsaveOpen, setConfirmUnsaveOpen] = useState(false);
  // Save asks "I own it" or "Wishlist" (#252: no silent default).
  const [saveSheetOpen, setSaveSheetOpen] = useState(false);
  const [savedIntent, setSavedIntent] = useState<VaultIntent | null>(null);
  const [savingIntent, setSavingIntent] = useState<VaultIntent | null>(null);
  const [burst, setBurst] = useState(0);
  // The "Verified" sheet: the public record, the photos, earlier versions (#283).
  const [verifiedOpen, setVerifiedOpen] = useState(false);
  const isConsumer = isAuthenticated && userProfile?.accountType === 'consumer';

  // Signed-in brand (client) users don't vault; absent accountType on an
  // authenticated user is a legacy client row. Only classify once the profile
  // has loaded: AuthContext flips isAuthenticated before fetching it, and a
  // consumer tapping Save in that window must not be told they're a brand.
  const isClientViewer =
    isAuthenticated && !!userProfile && userProfile.accountType !== 'consumer';

  // Main product data (for sub-products): its version count and on-chain data
  const [mainProductData, setMainProductData] = useState<PublicMainProduct | null>(null);
  const [mainProductBlockchainData, setMainProductBlockchainData] = useState<any>(null);

  const mainProductVersion = useProductVersion({
    productId: product?.mainProductId,
    initialVersion: 0,
    totalVersions: mainProductData?.versionCount || 1
  });

  const itemVersion = useProductVersion({
    productId: id,
    initialVersion: 0,
    totalVersions: product?.versionCount || 1
  });

  const isMobile = useIsMobile();

  // The page follows the shopper's theme: the phone's, or what they picked in
  // the vault (one switch for the vault, the product page and the share link).
  const { theme } = useVaultTheme();
  // A brand that set its colours lays the mat in them (#462).
  useThemeChrome(theme, brandBackground(product?.creator?.brand, theme));

  // Lets the VersionHistoryWidget register its cycle callback up here
  const cycleVersionsRef = useRef<(() => void) | null>(null);
  const handleSetCycleVersions = useCallback((fn: () => void) => {
    cycleVersionsRef.current = fn;
  }, []);

  const imageCarouselRef = useRef<ImageCarouselRef>(null);


  const isItem = product?.type === 'sub';

  // Determine which blockchain object ID to use for verification
  const blockchainObjectId = isItem && product?.objectId
    ? product.objectId
    : product?.mainObjectId;

  // Check if viewing historical versions (block-specific)
  const isViewingMainHistory = mainProductVersion.currentVersion < (mainProductData?.versionCount || 1) && mainProductVersion.currentVersion > 0;
  const isViewingSubHistory = itemVersion.currentVersion < (product?.versionCount || 1) && itemVersion.currentVersion > 0;

  // Use version-specific data when viewing history, otherwise use latest
  const mainProductDisplayData = isViewingMainHistory ? mainProductVersion.versionData?.data : mainProductBlockchainData;
  const itemDisplayData = isViewingSubHistory ? itemVersion.versionData?.data : product?.blockchainData;

  // Extract main product field values from blockchain data
  const mainProductFields = useMemo(() => {
    if (!product?.collectionFields) return [];

    // For sub-products, use mainProductDisplayData; for main products, use itemDisplayData (which is product.blockchainData or version data)
    const dataToUse = isItem ? mainProductDisplayData : itemDisplayData;

    if (!dataToUse) return [];

    const blockchainFields = extractFields(dataToUse);
    if (!blockchainFields || Object.keys(blockchainFields).length === 0) return [];

    return product.collectionFields
      .filter(field => !field.isItemField)
      .map((fieldDef: CollectionField) => {
        // Use sanitizedName from collection definition, fallback to simple sanitization
        const fieldKey = getFieldKey(fieldDef);
        const rawValue = blockchainFields[fieldKey] || '';
        const value = formatValue(rawValue);
        return {
          name: fieldDef.name, // Display original name to user
          type: fieldDef.type,
          value,
        };
      })
      .filter(field => field.value && field.value !== '[]');
  }, [product?.collectionFields, mainProductDisplayData, itemDisplayData, isItem]);

  // The piece's name, as the vault names it: the collection's product name
  // field — "Name", or an older collection's first product field (#395; the
  // vault snapshot uses the same rule). For an item, its product's name. It
  // titles the page and leaves the field list; long text or no value keeps
  // the collection name.
  const nameField = useMemo(() => {
    const def = productNameField(product?.collectionFields);
    const field = def ? mainProductFields.find(f => f.name === def.name) : undefined;
    const value = field?.value.replace(/\*\*/g, '').replace(/^#+\s*/, '').trim();
    return field && value && value.length <= 90 && !value.includes('\n') ? { field, value } : null;
  }, [product?.collectionFields, mainProductFields]);
  const listedMainFields = nameField ? mainProductFields.filter(f => f !== nameField.field) : mainProductFields;

  // Extract sub-product field values from blockchain data
  const itemFields = useMemo(() => {
    if (!isItem || !product?.collectionFields || !itemDisplayData) return [];

    const blockchainFields = extractFields(itemDisplayData);
    if (!blockchainFields || Object.keys(blockchainFields).length === 0) return [];

    return product.collectionFields
      .filter(field => field.isItemField)
      .map((fieldDef: CollectionField) => {
        // Use sanitizedName from collection definition, fallback to simple sanitization
        const fieldKey = getFieldKey(fieldDef);
        const rawValue = blockchainFields[fieldKey] || '';
        const value = formatValue(rawValue);
        return {
          name: fieldDef.name, // Display original name to user
          type: fieldDef.type,
          value,
        };
      })
      .filter(field => field.value && field.value !== '[]');
  }, [isItem, product?.collectionFields, itemDisplayData]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);
      setMainProductBlockchainData(null);

      try {
        const data = await getPublicProduct(id);
        setProduct(data);
        setLoadedProductId(id);
        track('public_product_viewed', { productExists: true });
        setIsSaved(!!data.savedByViewer);
        setSavedIntent(data.savedByViewer ? data.savedIntent ?? 'own' : null);

        // Initialize sub-product version (for both main and sub-products, this is the product's version)
        itemVersion.setCurrentVersion(data.versionCount); // Start at latest version

        // Items carry their parent's data. Requesting the parent through
        // getPublicProduct would record a second scan, for the parent (#312).
        if (data.type === 'sub' && data.mainProduct) {
          setMainProductData(data.mainProduct);
          setMainProductBlockchainData(data.mainProduct.blockchainData);
          mainProductVersion.setCurrentVersion(data.mainProduct.versionCount); // Start at latest version
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product');
        track('public_product_viewed', { productExists: false });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Fetch image verification once per product id (after the product is loaded).
  // A cancel flag drops stale results if the id changes mid-flight.
  useEffect(() => {
    setImageVerification(null);
    if (!id || loadedProductId !== id) return;

    let cancelled = false;
    getImageVerification(id).then(result => {
      if (!cancelled) setImageVerification(result);
    });

    return () => {
      cancelled = true;
    };
  }, [id, loadedProductId]);

  // 'redirect' scan mode: forward silently once the product is loaded.
  // 'choice' keeps the interstitial; 'verify' falls through to the product page.
  useEffect(() => {
    if (!product) return;
    if ((product.scanMode ?? 'basiqr') !== 'redirect') return;
    if (searchParams.get('verify') === '1') return;
    const dest = product.destinationUrl;
    if (dest && /^https:\/\//i.test(dest)) window.location.replace(dest);
  }, [product, searchParams]);

  // Inject <link rel="preconnect"> for the CloudFront image CDN domain
  useEffect(() => {
    if (!product?.images?.length) return;
    const firstUrl = product.images[0]?.url;
    if (!firstUrl) return;
    try {
      const origin = new URL(firstUrl).origin;
      // Skip if already present
      if (document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      document.head.appendChild(link);
      return () => { link.remove(); };
    } catch {
      // Invalid URL — skip
    }
  }, [product?.images]);

  // No public record, no sheet (#283: never a dead control).
  const handleVerify = useCallback(() => {
    if (blockchainObjectId) setVerifiedOpen(true);
  }, [blockchainObjectId]);
  const handleCycleVersion = useCallback(() => cycleVersionsRef.current?.(), []);

  const landed = useCallback((intent: VaultIntent) => {
    setIsSaved(true);
    setSavedIntent(intent);
    setBurst(b => b + 1);
    navigator.vibrate?.(15);
  }, []);

  // Complete a stashed save once the visitor is signed in (sign-up + code,
  // a log in, or a password reset), with the brand-news choice from sign-up.
  const completePendingSaveIntent = useCallback(
    async (allowBrand: boolean) => {
      const raw = sessionStorage.getItem(VAULT_INTENT_KEY);
      if (!raw) return;
      sessionStorage.removeItem(VAULT_INTENT_KEY);
      try {
        const pending = JSON.parse(raw) as {
          productId: string;
          folderId: string | null;
          intent?: VaultIntent;
          brandId?: string;
        };
        await saveItem(pending.productId, { folderId: pending.folderId ?? undefined, intent: pending.intent });
        if (allowBrand && pending.brandId) {
          setBrandAllowed(pending.brandId, true, 'signup').catch(err =>
            console.warn('Failed to record the brand-news choice:', err)
          );
        }
        if (product && pending.productId === product.id) {
          landed(pending.intent ?? 'own');
          setSaveSheetOpen(true);
        } else {
          showToast.custom('Saved to your vault', {
            action: { label: 'Open my vault', onClick: () => navigate('/vault') },
          });
        }
      } catch (err) {
        console.error('Failed to complete pending save:', err);
        showToast.error(vaultErrorMessage(err, 'Could not finish saving this product'));
      }
    },
    [product, navigate, landed]
  );

  const handleAuthSuccess = useCallback(
    (result?: { allowBrand: boolean }) => {
      setAuthSheetOpen(false);
      completePendingSaveIntent(!!result?.allowBrand);
    },
    [completePendingSaveIntent]
  );

  const handleSaveClick = useCallback(() => {
    // Auth still resolving (session check / profile fetch): the button is
    // disabled meanwhile — don't guess who the viewer is.
    if (!product || isAuthLoading) return;

    if (isClientViewer) {
      // Brand/client accounts can't vault (the API would 403) — explain
      // rather than hiding the button, which reads as a rendering bug.
      showToast.custom(
        'Saving is for personal shopper accounts — you are signed in with a brand account'
      );
      return;
    }
    setSaveSheetOpen(true);
  }, [product, isAuthLoading, isClientViewer]);

  // "I own it" / "Wishlist" in the save sheet: saves, or changes the choice
  // of a piece that is already saved. Signed-out visitors sign in first.
  const handleChoose = useCallback(
    async (intent: VaultIntent) => {
      if (!product) return;
      if (!isAuthenticated) {
        sessionStorage.setItem(
          VAULT_INTENT_KEY,
          JSON.stringify({ productId: product.id, folderId: null, intent, brandId: product.creator?.userId })
        );
        setSaveSheetOpen(false);
        setAuthSheetOpen(true);
        return;
      }
      if (isSaved && intent === savedIntent) return;
      setSavingIntent(intent);
      setIsSaving(true);
      try {
        if (isSaved) {
          await updateItem(product.id, { intent });
          setSavedIntent(intent);
        } else {
          await saveItem(product.id, { intent });
          landed(intent);
        }
      } catch (err) {
        console.error('Failed to save product:', err);
        showToast.error(vaultErrorMessage(err, 'Could not save this product'));
      } finally {
        setSavingIntent(null);
        setIsSaving(false);
      }
    },
    [product, isAuthenticated, isSaved, savedIntent, landed]
  );

  // Confirms "Remove from vault" from the save sheet.
  const handleConfirmUnsave = useCallback(async () => {
    if (!product) return;
    setIsSaving(true);
    try {
      await removeItem(product.id);
      setIsSaved(false);
      setSavedIntent(null);
      showToast.success('Removed from your vault');
      setConfirmUnsaveOpen(false);
      setSaveSheetOpen(false);
    } catch (err) {
      console.error('Failed to remove from vault:', err);
      showToast.error(vaultErrorMessage(err, 'Could not remove this product'));
    } finally {
      setIsSaving(false);
    }
  }, [product]);

  // A piece saved in the vault whose page has since been removed.
  const handleRemoveMissing = useCallback(async () => {
    if (!id) return;
    try {
      await removeItem(id);
      showToast.success('Removed from your vault');
      navigate('/vault');
    } catch (err) {
      showToast.error(vaultErrorMessage(err, 'Could not remove it. Try again.'));
    }
  }, [id, navigate]);

  // The save burst in the brand's thread and teal (the photo no longer tints the page).
  const burstColours = BURST_COLOURS;

  // Reset both blocks to latest versions
  const handleResetToLatest = useCallback(() => {
    if (mainProductData) {
      mainProductVersion.resetToLatest();
    }
    // itemVersion is the product's own version on main products, the sub-product's version on items
    if (product) {
      itemVersion.resetToLatest();
    }
  }, [mainProductData, product, mainProductVersion, itemVersion]);

  // The tab names the piece (#467), e.g. "Crew Sweatshirt — Sage · Marenne".
  const pieceTitle = product
    ? [nameField?.value || product.collectionName, product.creator?.displayName].filter(Boolean).join(' · ')
    : null;
  useDocumentTitle(pieceTitle);

  // Keys for the shortcut bar (#467): only the ones that do something on this
  // page. Earlier versions get H (further back) and Esc (back to the latest).
  const hasRecord = !!blockchainObjectId;
  const hasVersions = (product?.versionCount ?? 1) > 1 || (mainProductData?.versionCount ?? 1) > 1;
  const onOlderVersion = isViewingMainHistory || isViewingSubHistory;
  const shortcuts = useMemo<Shortcut[]>(() => {
    const list: Shortcut[] = [
      {
        id: 'photos',
        caps: ['←', '→'],
        label: 'Photos',
        keys: ['ArrowLeft', 'ArrowRight'],
        onPress: key => (key === 'ArrowLeft' ? imageCarouselRef.current?.goToPrevious() : imageCarouselRef.current?.goToNext()),
      },
    ];
    if (hasRecord) list.push({ id: 'verified', caps: ['V'], label: 'Verified', keys: ['v'], onPress: () => handleVerify() });
    if (hasVersions) list.push({ id: 'history', caps: ['H'], label: onOlderVersion ? 'Earlier still' : 'Earlier version', keys: ['h'], onPress: () => handleCycleVersion() });
    if (onOlderVersion) list.push({ id: 'latest', caps: ['Esc'], label: 'Latest', keys: ['Escape'], onPress: () => handleResetToLatest() });
    list.push({ id: 'save', caps: ['S'], label: isSaved ? 'In your vault' : 'Save', keys: ['s'], primary: !isSaved, onPress: () => handleSaveClick() });
    return list;
  }, [hasRecord, hasVersions, onOlderVersion, isSaved, handleVerify, handleCycleVersion, handleResetToLatest, handleSaveClick]);
  const anySheetOpen = authSheetOpen || saveSheetOpen || confirmUnsaveOpen || verifiedOpen;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return <ShopperLoading theme={theme} />;
  }

  // Opened from the vault but the brand has removed the page: say so and
  // offer to clear it, instead of the generic dead end (#252 F8).
  if (error && isConsumer && backTo?.path.startsWith('/vault')) {
    return (
      <div className="vault vault-page pp-gone" data-vault-theme={theme}>
        <EmptyHanger />
        <h1>No longer available</h1>
        <p>The brand has taken this piece's page down. It's still in your vault until you remove it.</p>
        <div className="pp-gone-actions">
          <button type="button" onClick={() => navigate(backTo.path)} className="v-btn v-btn-primary">
            Back to my vault
          </button>
          <button type="button" onClick={handleRemoveMissing} className="v-btn v-btn-danger-soft">
            Remove from my vault
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vault vault-page pp-gone" data-vault-theme={theme}>
        <EmptyHanger />
        <h1>This label has no page right now</h1>
        <p>The brand may have taken the page down or not finished it yet. If the piece is in your vault, it stays there.</p>
        <div className="pp-gone-actions">
          {isConsumer && (
            <button type="button" onClick={() => navigate('/vault')} className="v-btn v-btn-primary">
              <Shirt size={17} aria-hidden="true" />
              Open my vault
            </button>
          )}
          <a
            href={landingSiteUrl('product_not_found')}
            {...EXTERNAL_LINK_PROPS}
            onClick={() => trackLandingSiteClick('product_not_found')}
            className={`v-btn ${isConsumer ? 'v-btn-ghost' : 'v-btn-primary'}`}
          >
            What is BasiQR?
          </a>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Decide what the scan does. ?verify=1 ("Verify on BasiQR") and an unusable
  // destination both fall through to the product page below.
  const scanMode = product.scanMode ?? 'basiqr';
  const wantsVerify = searchParams.get('verify') === '1';
  const usableDest =
    product.destinationUrl && /^https:\/\//i.test(product.destinationUrl)
      ? product.destinationUrl
      : null;
  const goVerify = () => {
    const next = new URLSearchParams(searchParams);
    next.set('verify', '1');
    setSearchParams(next, { replace: true });
  };
  // 'redirect' forwards silently (the replace happens in the effect above); show
  // only a brief placeholder so the product page never flashes.
  if (!wantsVerify && usableDest && scanMode === 'redirect') {
    return <ShopperLoading theme={theme} label="Taking you to the brand’s page…" />;
  }
  if (!wantsVerify && usableDest && scanMode === 'choice') {
    return (
      <>
        <BrandColours brand={product.creator?.brand} />
        <ScanInterstitial
          product={product}
          destination={usableDest}
          countdownSeconds={product.interstitial?.countdownSeconds ?? 10}
          onVerify={goVerify}
        />
      </>
    );
  }

  // The trust control (#283): amber while an earlier version is on screen or
  // a photo changed; absent without a public record.
  const viewingHistory = isViewingMainHistory || isViewingSubHistory;
  const trustState: TrustState | null = !blockchainObjectId
    ? null
    : viewingHistory
      ? 'history'
      : imageVerification?.overall === 'mismatch'
        ? 'changed'
        : 'verified';
  const itemTotal = product.versionCount || 1;
  const mainTotal = mainProductData?.versionCount || 1;
  const versionUpdates = itemTotal - 1 + (isItem ? mainTotal - 1 : 0);
  const atOldest = itemVersion.currentVersion <= 1 && (!isItem || mainProductVersion.currentVersion <= 1);
  const sheetVersions: VerifiedVersions | null =
    versionUpdates > 0
      ? {
          updates: versionUpdates,
          viewingOlder: viewingHistory,
          position: viewingHistory
            ? itemTotal > 1
              ? `Version ${itemVersion.currentVersion} of ${itemTotal}`
              : `Version ${mainProductVersion.currentVersion} of ${mainTotal}`
            : undefined,
          canGoOlder: !atOldest,
          onOlder: handleCycleVersion,
          onLatest: handleResetToLatest,
        }
      : null;

  // The page's sections (#467): the brand's fields grouped by name, like the
  // vault card's back; a field that matches no section stays a row.
  const piece = nameField ? splitName(nameField.value) : null;
  const sections = groupFields(listedMainFields.map(({ name, value }) => ({ name, value })));
  const careIds = careSymbols(sections.care.flatMap(f => careLines(f.value)));
  const plain = (value: string) => value.replace(/\*\*/g, '').trim();
  const md = (value: string) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]} className="pp-md">
      {value}
    </ReactMarkdown>
  );
  const brandName = product.creator?.displayName ?? 'The brand';
  const fieldsKey = `${mainProductVersion.currentVersion}-${itemVersion.currentVersion}`;
  const ticketLabel =
    trustState === 'history'
      ? { title: 'Earlier version', sub: sheetVersions?.position ?? 'Not the latest' }
      : trustState === 'changed'
        ? { title: 'Photo changed', sub: 'since it was registered' }
        : { title: 'Verified', sub: 'Checked on scan' };

  return (
    <VaultThemeContext.Provider value={theme}>
      <BrandColours brand={product.creator?.brand} />
      <div className="vault vault-page pp" data-vault-theme={theme}>
        <header className="pp-top">
          {backTo ? (
            <button type="button" className="pp-pill" onClick={() => navigate(backTo.path)} aria-label={`Back to ${backTo.label}`}>
              <ArrowLeft size={16} aria-hidden="true" />
              Back to {backTo.label}
            </button>
          ) : (
            // The brand's own logo when it has one (#462); BasiQR then stays in the footer.
            <BrandLabel
              brand={product.creator?.brand}
              name={brandName}
              fallback={
                <a
                  className="pp-logo"
                  href={landingSiteUrl('product_page')}
                  {...EXTERNAL_LINK_PROPS}
                  onClick={() => trackLandingSiteClick('product_page')}
                  aria-label="BasiQR"
                >
                  <img src="/BasiQR-icon.svg" alt="" width={30} height={30} />
                </a>
              }
            />
          )}
          {isConsumer && !backTo?.path.startsWith('/vault') && (
            <button type="button" className="pp-pill" onClick={() => navigate('/vault')}>
              <Shirt size={16} aria-hidden="true" />
              My vault
            </button>
          )}
        </header>

        {/* An earlier version is on screen: say which, and the way back (#283) */}
        {viewingHistory && (
          <div className="pp-banner" role="status">
            <History size={16} aria-hidden="true" />
            <span>
              You're looking at <b>{(sheetVersions?.position ?? 'an earlier version').toLowerCase()}</b>. {brandName} updated this page since.
            </span>
            <button type="button" onClick={handleResetToLatest}>
              Back to the latest
            </button>
          </div>
        )}

        <main className="pp-grid">
          <section className="pp-media" aria-label="Photos">
            <div className="pp-print">
              <ImageCarousel
                ref={imageCarouselRef}
                images={product.images}
                productName={nameField?.value || product.collectionName}
                isMobile={isMobile}
                showCount={!trustState}
              />
            </div>
            {/* The woven tag on the photo (the landing hero's): a second way into
                the Verified sheet for a pointer; the button below is the one
                screen readers and keyboards use. */}
            {trustState && (
              <button type="button" className="pp-ticket" data-trust={trustState} onClick={handleVerify} aria-hidden="true" tabIndex={-1}>
                {trustState === 'verified' ? <Check size={17} /> : trustState === 'history' ? <History size={17} /> : <AlertTriangle size={17} />}
                <span>
                  <b>{ticketLabel.title}</b>
                  <small>{ticketLabel.sub}</small>
                </span>
              </button>
            )}
          </section>

          <section className="pp-info">
            <h1 className="pp-title">{piece ? piece.base : product.collectionName}</h1>
            {piece && <p className="pp-sub">{[piece.colour, product.collectionName].filter(Boolean).join(' · ')}</p>}

            <div className="pp-brandrow">
              <CreatorInfo creator={product.creator} />
              {sections.price && (
                <p className={`pp-price${viewingHistory ? ' is-old' : ''}`}>
                  <span className="sr-only">{sections.price.name}</span>
                  {plain(sections.price.value)}
                </p>
              )}
            </div>

            {!isMobile && (
              <div className="pp-actions">
                <ProductActions
                  variant="inline"
                  trust={trustState}
                  onTrust={handleVerify}
                  onSave={handleSaveClick}
                  isSaved={isSaved}
                  isSaving={isSaving}
                  saveDisabled={isAuthLoading}
                  burst={burst}
                  burstColours={burstColours}
                />
              </div>
            )}

            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={fieldsKey} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                {isItem && itemFields.length > 0 && (
                  <section className="pp-sec pp-piece" aria-label="This piece">
                    <h2>This piece</h2>
                    <dl>
                      {itemFields.map((f, i) => (
                        <div key={i}>
                          <dt>{f.name}</dt>
                          <dd>{plain(f.value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                )}

                {sections.care.length > 0 && (
                  <section className="pp-sec">
                    <h2>Washing &amp; care</h2>
                    {careIds.length > 0 ? (
                      <>
                        <ul className="pp-care">
                          {careIds.map(cid => (
                            <li key={cid}>
                              <CareSymbol id={cid} size={26} />
                              <span>{CARE_LABELS[cid]}</span>
                            </li>
                          ))}
                        </ul>
                        {sections.care.map((f, i) => (
                          <details className="pp-more" key={i}>
                            <summary>
                              {sections.care.length > 1 ? f.name : 'All care notes'}
                              <ChevronDown size={15} aria-hidden="true" />
                            </summary>
                            {md(f.value)}
                          </details>
                        ))}
                      </>
                    ) : (
                      sections.care.map((f, i) => <div key={i}>{md(f.value)}</div>)
                    )}
                  </section>
                )}

                {sections.fit.length > 0 && (
                  <section className="pp-sec">
                    <h2>Size &amp; fit</h2>
                    {sections.fit.map((f, i) => (
                      <div className="pp-story" key={i}>
                        <h3>{f.name}</h3>
                        {md(f.value)}
                      </div>
                    ))}
                  </section>
                )}

                {sections.materials.length > 0 && (
                  <section className="pp-sec">
                    <h2>Materials</h2>
                    {sections.materials.map((f, i) => (
                      <div className="pp-mat" key={i}>
                        <h3>{f.name}</h3>
                        {md(f.value)}
                      </div>
                    ))}
                  </section>
                )}

                {sections.made.length > 0 && (
                  <section className="pp-sec">
                    <h2>Where it’s made</h2>
                    {sections.made.map((f, i) => (
                      <div className="pp-made" key={i}>
                        <MapPin size={17} aria-hidden="true" />
                        {md(f.value)}
                      </div>
                    ))}
                  </section>
                )}

                {sections.story.length > 0 && (
                  <section className="pp-sec">
                    <h2>The story</h2>
                    {sections.story.map((f, i) => (
                      <div className="pp-story" key={i}>
                        <h3>{f.name}</h3>
                        {md(f.value)}
                      </div>
                    ))}
                  </section>
                )}

                {sections.repairs.length > 0 && (
                  <section className="pp-sec">
                    <h2>Repairs</h2>
                    {sections.repairs.map((f, i) => (
                      <div className="pp-story" key={i}>
                        {sections.repairs.length > 1 && <h3>{f.name}</h3>}
                        {md(f.value)}
                      </div>
                    ))}
                  </section>
                )}

                {sections.details.length > 0 && (
                  <section className="pp-sec">
                    <h2>More details</h2>
                    <dl className="pp-rows">
                      {sections.details.map((f, i) => (
                        <div key={i}>
                          <dt>{f.name}</dt>
                          <dd>{md(f.value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="pp-foot">
              <span>
                {`Added on ${formatDate(product.createdAt)}`}
              </span>
              <PoweredByBasiQR source="product_page" className="pp-powered" />
            </div>
          </section>
        </main>

        {/* The shortcut bar (#467): only keys that work here, sewn on bottom left */}
        {!isMobile && <ShortcutBar shortcuts={shortcuts} disabled={anySheetOpen} />}

        {/* Steps through earlier versions for H and the Verified sheet; its own hint stays off */}
        {hasVersions && (
          <VersionHistoryWidget
            mainProductVersion={mainProductVersion}
            itemVersion={itemVersion}
            isItem={isItem}
            mainProductName={(mainProductData && product.collectionName) || 'Main Product'}
            itemName={product.collectionName || 'This Item'}
            showKeyboardHint={false}
            onCycleVersions={handleSetCycleVersions}
          />
        )}

        {isMobile && (
          <ProductActions
            variant="dock"
            trust={trustState}
            onTrust={handleVerify}
            onSave={handleSaveClick}
            isSaved={isSaved}
            isSaving={isSaving}
            saveDisabled={isAuthLoading}
            burst={burst}
            burstColours={burstColours}
          />
        )}
      </div>

      <VerifiedSheet
        productId={verifiedOpen ? id ?? null : null}
        brandName={product.creator?.displayName}
        onClose={() => setVerifiedOpen(false)}
        product={product}
        images={imageVerification}
        versions={sheetVersions}
      />

      <SaveSheet
        open={saveSheetOpen}
        onClose={() => setSaveSheetOpen(false)}
        productName={nameField?.value || product.collectionName}
        imageUrl={product.images?.[0]?.url}
        brandName={product.creator?.displayName}
        brandId={product.creator?.userId}
        saved={isSaved}
        intent={savedIntent}
        saving={savingIntent}
        signedIn={isAuthenticated}
        onChoose={handleChoose}
        onRemove={() => setConfirmUnsaveOpen(true)}
        onViewVault={() => navigate('/vault')}
      />

      {/* Consumer Vault auth sheet — opened when a signed-out visitor saves */}
      <ConsumerAuthSheet
        open={authSheetOpen}
        onClose={() => setAuthSheetOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        brandName={product.creator?.displayName}
        imageUrl={product.images?.[0]?.url}
      />

      <ConfirmSheet
        open={confirmUnsaveOpen}
        title="Remove from your vault?"
        description="Scan its label again to bring it back."
        confirmLabel="Remove"
        loading={isSaving}
        onConfirm={handleConfirmUnsave}
        onCancel={() => setConfirmUnsaveOpen(false)}
      />
    </VaultThemeContext.Provider>
  );
};

export default ProductContent;
